import {
  getListPlatformJobsQueryKey,
  runPlatformJob,
  setPlatformJobsPaused,
  stopPlatformJob,
  updatePlatformJob,
  useListPlatformJobs,
  type JobOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { required, type Field, type FormValues } from '../../components/FormFields';
import { StatRow, StatTile } from '../../components/Stats';
import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/format';
import { timezoneLabel } from '../../lib/timezones';
import { RunResult, seconds } from './jobLabels';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const clock = (hour: number) => `${hour % 12 || 12} ${hour < 12 ? 'AM' : 'PM'}`;

const FIELDS: Field[] = [
  {
    name: 'hour',
    label: 'Hour',
    type: 'select',
    required: true,
    options: Array.from({ length: 24 }, (_, hour) => ({
      value: String(hour),
      label: clock(hour),
    })),
  },
  {
    name: 'minute',
    label: 'Minute',
    type: 'select',
    required: true,
    options: ['0', '15', '30', '45'].map((value) => ({
      value,
      label: `:${value.padStart(2, '0')}`,
    })),
  },
  ...DAYS.map((day) => ({
    name: `day${DAYS.indexOf(day)}`,
    label: day,
    type: 'checkbox' as const,
  })),
];

function toValues(job: JobOut): FormValues {
  return {
    hour: String(job.hour),
    minute: String(job.minute),
    ...Object.fromEntries(DAYS.map((_, index) => [`day${index}`, job.days.includes(index)])),
  };
}

function JobState({ job, paused }: { job: JobOut; paused: boolean }) {
  if (job.stop_requested_at) return <Chip size="small" color="warning" label="Stopping" />;
  if (job.running_since) return <Chip size="small" color="info" label="Running" />;
  if (paused) return <Chip size="small" color="warning" label="Paused" />;
  if (job.run_requested_at) return <Chip size="small" color="info" label="Starting Soon" />;
  return job.enabled ? (
    <Chip size="small" color="success" label="On" />
  ) : (
    <Chip size="small" label="Off" />
  );
}

export function ScheduledJobsPage() {
  const queryClient = useQueryClient();
  const query = useListPlatformJobs({
    query: {
      // Only while something is happening. A page left open shouldn't keep querying the database.
      refetchInterval: (latest) => {
        const data = okData(latest.state.data);
        return data?.items.some((j) => j.running_since || j.run_requested_at) ? 30000 : false;
      },
    },
  });
  const data = okData(query.data);
  const [editing, setEditing] = useState<JobOut | null>(null);
  const [pausing, setPausing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: getListPlatformJobsQueryKey() });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const items = data?.items ?? [];
  const paused = data?.paused ?? false;
  const failures = items.reduce((total, job) => total + job.failures_last_7_days, 0);
  const columns: DataColumn<JobOut>[] = [
    {
      label: 'Job',
      render: (j) => (
        <>
          {j.label}
          <Typography variant="body2" color="text.secondary">
            {j.description}
          </Typography>
        </>
      ),
    },
    { label: 'Status', render: (j) => <JobState job={j} paused={paused} /> },
    { label: 'Schedule', render: (j) => j.schedule },
    {
      label: 'Next Run',
      render: (j) => (j.next_run_at && !paused ? formatDateTime(j.next_run_at) : '–'),
    },
    {
      label: 'Last Run',
      render: (j) =>
        j.last_run ? (
          <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <span>{formatDateTime(j.last_run.started_at)}</span>
            <RunResult run={j.last_run} />
          </Stack>
        ) : (
          'Not yet'
        ),
    },
    {
      label: 'Last 7 Days',
      render: (j) => (
        <>
          {j.runs_last_7_days} {j.runs_last_7_days === 1 ? 'run' : 'runs'}
          {j.failures_last_7_days > 0 && (
            <Typography variant="body2" color="error">
              {j.failures_last_7_days} failed
            </Typography>
          )}
          {j.average_seconds !== null && (
            <Typography variant="body2" color="text.secondary">
              About {seconds(j.average_seconds)}
            </Typography>
          )}
        </>
      ),
    },
    {
      label: 'On',
      render: (j) => (
        <Switch
          size="small"
          checked={j.enabled}
          disabled={busy}
          slotProps={{ input: { 'aria-label': `${j.label} on or off` } }}
          onChange={(e) =>
            void act(() =>
              updatePlatformJob(j.name, {
                enabled: e.target.checked,
                hour: j.hour,
                minute: j.minute as 0 | 15 | 30 | 45,
                days: j.days,
              }),
            )
          }
        />
      ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (j) => (
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', whiteSpace: 'nowrap' }}
        >
          <Button size="small" onClick={() => setEditing(j)}>
            Edit
          </Button>
          {j.running_since ? (
            <Button
              size="small"
              color="error"
              disabled={busy || !!j.stop_requested_at}
              onClick={() => void act(() => stopPlatformJob(j.name))}
            >
              Stop
            </Button>
          ) : (
            <Tooltip title="Runs within 15 minutes, once">
              <span>
                <Button
                  size="small"
                  disabled={busy || paused || !!j.run_requested_at}
                  onClick={() => void act(() => runPlatformJob(j.name))}
                >
                  Run Now
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {paused && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() => void act(() => setPlatformJobsPaused({ paused: false }))}
            >
              Resume All
            </Button>
          }
        >
          All jobs are paused. Rent won&apos;t post and reminders won&apos;t go out until you
          resume.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <StatRow>
        <StatTile label="Jobs On" value={data && items.filter((j) => j.enabled).length} />
        <StatTile label="Failures, Last 7 Days" value={data && failures} />
        <StatTile
          label="Scheduler Last Woke"
          value={
            data &&
            (data.scheduler_last_tick ? formatDateTime(data.scheduler_last_tick) : 'Not Yet')
          }
        />
      </StatRow>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h6">Scheduled Job Runs</Typography>
          <Typography variant="body2" color="text.secondary">
            {data && `Times are ${timezoneLabel(data.timezone)}. `}A job that&apos;s off makes one
            small check when it would have run, and does no work.
          </Typography>
        </div>
        {!paused && (
          <Button color="error" variant="outlined" disabled={busy} onClick={() => setPausing(true)}>
            Pause All Jobs
          </Button>
        )}
      </Stack>
      <DataTable columns={columns} rows={items} rowKey={(j) => j.name} />
      {editing && (
        <FormDialog
          title={`Edit ${editing.label}`}
          intro="Choose when this job runs. Changes apply from the scheduler's next wake-up, within 15 minutes."
          fields={FIELDS}
          initial={toValues(editing)}
          onSubmit={(values) =>
            updatePlatformJob(editing.name, {
              enabled: editing.enabled,
              hour: Number(required(values, 'hour')),
              minute: Number(required(values, 'minute')) as 0 | 15 | 30 | 45,
              days: DAYS.flatMap((_, index) => (values[`day${index}`] ? [index] : [])),
            })
          }
          onClose={() => setEditing(null)}
        />
      )}
      {pausing && (
        <FormDialog
          title="Pause All Jobs?"
          intro="Nothing new starts, and a running job stops after the business it's on. Rent won't post, and reminders and alerts won't go out, until you resume. Rent and late fees catch up only if you resume on the same day."
          fields={[]}
          initial={{}}
          submitLabel="Pause All Jobs"
          onSubmit={() => setPlatformJobsPaused({ paused: true })}
          onClose={() => setPausing(false)}
        />
      )}
    </Stack>
  );
}
